<template>
  <div>
    <v-snackbar
      v-model="notification.show"
      :color="notification.color"
    >
      {{ notification.text }}
    </v-snackbar>
    <v-data-table
      :headers="headers"
      :items="ruleHistoryItems"
      :loading="loading"
      :server-items-length="totalItems"
      :options.sync="pagination"
      :footer-props="{ itemsPerPageOptions: [5, 10, 15, -1], showFirstLastPage: true }"
      @pagination="changeRowBackgroundColor"
    >
      <template v-slot:top>
        <export-button
          id="audit-log-button-export"
          @clickCsv="exportAuditLog('csv')"
          @clickXls="exportAuditLog('xls')"
        />
      </template>

      <template v-slot:header.program.code="{ header }">
        <div>{{ header.text }}</div>
        <v-text-field
          v-model="programFilterValue"
          prepend-icon="mdi-filter-variant"
          type="text"
          clearable
          @click.stop
        />
      </template>
      <template v-slot:header.updatedByUsername="{ header }">
        <div>{{ header.text }}</div>
        <v-text-field
          v-model="updatedByFilterValue"
          prepend-icon="mdi-filter-variant"
          type="text"
          clearable
          @click.stop
        />
      </template>

      <template v-slot:item.validFrom="{ item }">
        {{ getDateTimeInET(item.validFrom) }}
      </template>
      <template v-slot:item.validTo="{ item }">
        {{ getDateTimeInET(item.validTo) }}
      </template>
      <template v-slot:item.active="{ item }">
        <span v-if="item.active">Active</span>
        <span
          v-else
          class="inactive"
        >Inactive</span>
      </template>
      <template v-slot:item.transactionType="{ item }">
        {{ item.transactionType }}
      </template>
      <template v-slot:item.timeRangeHours="{ item }">
        <span v-if="item.active">{{ item.timeRangeHours }}</span>
        <span v-else>-</span>
      </template>
      <template v-slot:item.transactionCount="{ item }">
        <span v-if="item.active">{{ item.transactionCount }}</span>
        <span v-else>-</span>
      </template>
      <template v-slot:item.includedIdsRanges="{ item }">
        <range-list-cell
          v-if="item.active"
          :range-list="item.includedIdsRanges"
        />
        <span v-else>-</span>
      </template>
      <template v-slot:item.excludedIdsRanges="{ item }">
        <range-list-cell
          v-if="item.active"
          :range-list="item.excludedIdsRanges"
        />
        <span v-else>-</span>
      </template>
    </v-data-table>
  </div>
</template>

<script>
  import { api } from '../http/api'
  import dateUtils from '../util/dateUtils'

  export default {
    name: 'AuditLog',

    components: {
      RangeListCell: () => import('./RangeListCell'),
      ExportButton: () => import('./ExportButton'),
    },

    props: {
      ruleId: String,
      transactionType: String,
    },

    data () {
      return {
        loading: false,
        headers: [
          {
            text: 'Program ID',
            align: 'from',
            value: 'program.code',
            filter: this.programFilter,
            class: 'auditlog-table-header-program filterable-table-header',
          },
          {
            text: 'From (ET)',
            value: 'validFrom',
            class: 'auditlog-table-header-validfrom',
          },
          {
            text: 'To (ET)',
            value: 'validTo',
            class: 'auditlog-table-header-validto',
          },
          {
            text: 'Status',
            value: 'active',
            class: 'auditlog-table-header-active',
          },
          {
            text: 'Transaction Type',
            value: 'transactionType',
            class: 'auditlog-table-header-txntype',
          },
          {
            text: 'Hours',
            value: 'timeRangeHours',
            class: 'auditlog-table-header-timerangehours',
          },
          {
            text: 'Transactions',
            value: 'transactionCount',
            class: 'auditlog-table-header-transactioncount',
          },
          {
            text: 'Inclusions',
            sortable: false,
            value: 'includedIdsRanges',
            class: 'auditlog-table-header-inclusions',
          },
          {
            text: 'Exclusions',
            sortable: false,
            value: 'excludedIdsRanges',
            class: 'auditlog-table-header--exclusions',
          },
          {
            text: 'Updated By',
            value: 'updatedByUsername',
            class: 'auditlog-table-header-updatedbyusername filterable-table-header',
            filter: this.updatedByFilter,
          },
        ],
        ruleHistoryItems: [],
        totalItems: 0,
        // Per-rule audit defaults to validFrom desc; general audit to program then validFrom
        // (server applies the single primary sort column).
        pagination: {
          page: 1,
          itemsPerPage: 10,
          sortBy: this.ruleId ? ['validFrom'] : ['program.code'],
          sortDesc: this.ruleId ? [true] : [false],
        },
        programFilterValue: null,
        updatedByFilterValue: null,
        notification: {
          show: false,
          text: '',
          color: 'success', // ['success', 'info', 'error']
        },
      }
    },

    watch: {
      pagination: {
        deep: true,
        handler () {
          this.fetchHistory()
        },
      },
      programFilterValue () {
        this.onFilterChange()
      },
      updatedByFilterValue () {
        this.onFilterChange()
      },
    },

    created () {
      // Resolve transactionType before the table's initial options emit triggers the fetch.
      if (this.transactionType) {
        sessionStorage.setItem('transactionType', this.transactionType)
      }
      if (!this.transactionType) {
        this.transactionType = sessionStorage.getItem('transactionType')
      }
    },

    methods: {
      // Debounced reset-to-page-1 on filter change; the pagination watcher performs the fetch
      // (server-side filtering, since Vuetify ignores client-side header filters in server mode).
      onFilterChange () {
        clearTimeout(this.filterDebounce)
        this.filterDebounce = setTimeout(() => {
          this.pagination = { ...this.pagination, page: 1 }
        }, 400)
      },
      fetchHistory () {
        this.loading = true
        const { page, itemsPerPage, sortBy, sortDesc } = this.pagination
        const fallbackSort = this.ruleId ? 'validFrom' : 'program.code'
        const fallbackDesc = !!this.ruleId
        const sortColumn = Array.isArray(sortBy) && sortBy.length > 0 ? sortBy[0] : fallbackSort
        const sortDirection = Array.isArray(sortDesc) && sortDesc.length > 0 ? sortDesc[0] : fallbackDesc
        // Vuetify uses -1 for the "All" option; translate to a large page size for the server-side query.
        const size = itemsPerPage === -1 ? 100000 : itemsPerPage
        api.getRuleHistoryItems(this.ruleId, this.transactionType, page, size, sortColumn, sortDirection,
          this.programFilterValue, this.updatedByFilterValue)
          .then(value => {
            this.ruleHistoryItems = value.ruleHistoryItems
            this.totalItems = value.totalRecordsCount
          }).catch(err => {
            this.showErrorMessage(err)
          }).finally(() => {
            this.loading = false
          })
      },
      changeRowBackgroundColor () {
        setTimeout(() => {
          const rows = document.querySelectorAll('table tbody tr')
          rows.forEach(x => {
            const special = x.querySelector('span.inactive')
            if (special) {
              x.setAttribute('style', 'background-color: lightgray')
            }
          })
        }, 200)
      },
      getDateTimeInET: (dateString) => {
        return dateUtils.getDateTimeInET(dateString)
      },
      exportAuditLog (format) {
        api.exportRuleHistoryItems(this.ruleId, this.transactionType, format)
      },
      programFilter (value) {
        if (!this.programFilterValue) {
          return true
        }
        if (!value) {
          return false
        }
        return value.toLowerCase().includes(this.programFilterValue.toLowerCase())
      },
      updatedByFilter (value) {
        if (!this.updatedByFilterValue) {
          return true
        }
        if (!value) {
          return false
        }
        return value.toLowerCase().includes(this.updatedByFilterValue.toLowerCase())
      },
      showErrorMessage (err) {
        if (err.data?.message) {
          this.notification = { show: true, text: err.data.message, color: 'error' }
        } else {
          this.notification = { show: true, text: 'Something went wrong.', color: 'error' }
        }
      },
    },
  }
</script>
